import React, { lazy, Suspense, useState } from 'react';
import styled, { css } from 'styled-components';
import Layout from 'src/presentation/components/Layout';
import breakpoints from 'src/integration/styles/breakpoints';
import WhiteBox from 'src/presentation/components/ui/WhiteBox';
import colors from 'src/integration/styles/colors';
import Header from 'src/presentation/components/ui/Header';
import { CategoryScale } from 'chart.js';
import Chart from 'chart.js/auto';
import BatteryDataTable from 'src/presentation/components/DataTables/BatteryDataTable';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Icon from 'src/presentation/components/ui/Icon';
import { iconDots, imageBattery } from 'src/integration/assets/AssetsProvider';
import Button from 'src/presentation/components/ui/Button';
import CustomRadioGroup from 'src/presentation/components/ui/CustomRadioGroup';
import { RevenueOptionsType } from 'src/integration/types/RevenueOptionsType';
import FleetNavigation from 'src/presentation/components/DateNavigation/FleetNavigation';
import { useStore } from 'src/integration/store/store';
import useFetchChartSocAndBalancing from 'src/integration/api/useFetchChartSocAndBalancing';
import useFetchChartPowerAndBalancing from 'src/integration/api/useFetchChartPowerAndBalancing';
import useFetchChartFrequencyAndBalancing from 'src/integration/api/useFetchChartFrequencyAndBalancing';
import useFetchRevenue from 'src/integration/api/useFetchRevenue';
import useFetchBattery from 'src/integration/api/useFetchBattery';
import fonts from 'src/integration/styles/fonts';
import useFetchRevenueTotal from 'src/integration/api/useFetchRevenueTotal';
import { observer } from 'mobx-react-lite';
import useFetchSites from 'src/integration/api/useFetchSites';
import useFormattingUtils from 'src/toolkit/utils/useFormattingUtils';
import RevenueDonutChart from 'src/presentation/components/Charts/RevenueDonutChart';
import StackedBarChart from 'src/presentation/components/Charts/StackedBarChart';
import useFetchFfrEnergyPrice from 'src/integration/api/useFetchFfrEnergyPrice';
import useFetchFfrCapacityPrice from 'src/integration/api/useFetchFfrCapacityPrice';
import useFetchFfrEnergyVolumes from 'src/integration/api/useFetchFfrEnergyVolumes';
import useFetchFfrCapacityVolumes from 'src/integration/api/useFetchFfrCapacityVolumes';
import useNewDemoPageCsvExports from 'src/toolkit/hooks/useNewDemoPageCsvExports';
import useFetchRevenueByMarket from 'src/integration/api/useFetchRevenueByMarket';

const RevenueBarChart = lazy(() => import("src/presentation/components/Charts/RevenueBarChart"));
const SocAndBalancingStepChart = lazy(() => import("src/presentation/components/Charts/StepChart"));
const PowerAndBalancingStepChart = lazy(() => import("src/presentation/components/Charts/PowerAndBalancingStepChart"));
const FrequencyAndBalancingStepChart = lazy(() => import("src/presentation/components/Charts/FrequencyAndBalancingStepChart"));

type GridProps = {
  $reverse?: boolean;
  $oneColumn?: boolean;
  $revenueColumns?: boolean;
  $batteryColumns?: boolean;
};

const getGridTemplateColumns = ({ $reverse, $oneColumn, $revenueColumns, $batteryColumns }: GridProps) => {
  if ($oneColumn) {
    return '1fr';
  }

  if ($revenueColumns) {
    return '1.5fr 2fr 1fr';
  }

  if ($batteryColumns || $reverse) {
    return '3fr 2fr';
  }

  return null;
};

const Grid = styled.div<GridProps>`
  ${({ $reverse, $oneColumn, $revenueColumns, $batteryColumns }) => {
    const columns = getGridTemplateColumns({ $reverse, $oneColumn, $revenueColumns, $batteryColumns });

    return css`
      display: grid;
      flex-direction: column;
      gap: 1.5rem;
      align-items: stretch;
      margin-bottom: 1.5rem;
      grid-template-columns: ${columns ?? '1fr'};

      ${breakpoints.mobile`
        display: flex;
      `}

      ${breakpoints.desktop`
        display: grid;
        gap: 1.5rem;
        margin-bottom: 2.5rem;
        grid-template-columns: ${columns ?? '1fr'};
      `}

      ${breakpoints.desktopLarge`
        display: grid;
        gap: 1.5rem;
        margin-bottom: 2.5rem;
        grid-template-columns: ${columns ?? '1fr 1fr'};
      `}

      ${breakpoints.desktopLarge4K`
        display: grid;
        gap: 1.5rem;
        margin-bottom: 2.5rem;
        grid-template-columns: ${columns ?? '1fr 1fr'};
      `}
    `;
  }}
`;

const Grid4k = styled.div`
  ${breakpoints.desktopLarge4K`
    display: grid;
    gap: 1.5rem;
    margin-bottom: 2.5rem;
    grid-template-columns: 1fr 1fr;
  `}
`;

const Column = styled.div`
  ${() => css`
    display: flex;
    flex-direction: column;

    ${breakpoints.desktop`
      gap: 0.75rem;
    `}
  `}
`;

const whiteTextMixin = css`
    color: ${colors.primaryWhite};
`;

const headingFontMixin = css`
    font-family: ${fonts.fontFamily.headings};
    font-weight: 400;
`;

const Price = styled(Header)`
    ${whiteTextMixin};
    font-size: 5rem;
    line-height: 100%;
`;

const Currency = styled(Header)`
    ${whiteTextMixin};
    font-size: 1.875rem;
`;

const InlineText = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
`;

const InlineTextJustified = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const TitleWhiteBox = styled(WhiteBox)`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 1.5rem;
    height: 100%;
`;

const ScrollTableWhiteBox = styled(WhiteBox)`
    overflow: scroll;
`;

const FlexboxRow = styled.div<{ $mb?: number}>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: ${({$mb = 0}) => `${$mb / 16}rem`};
`;

const PositionAbsoluteHeader = styled.div<{ $mr?: number}>`
    position: absolute;
    right: ${({$mr = 24}) => `${$mr / 16}rem`};
`;

const StyledHeader = styled(Header)`
    ${whiteTextMixin};
`;

const SectionHeader = styled(Header)`
    padding-bottom: 0.75rem;
    padding-left: 1.5rem;
`;

const FfrPriceColumns = styled.div`
  ${breakpoints.desktopLarge`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: start;
  `}

  ${breakpoints.desktopLarge4K`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: start;
  `}
`;

const RevenueBarChartArea = styled.div`
    height: 15.625rem;
`;

const StepChartArea = styled.div`
    height: 17.5rem;
`;

const AmountHeader = styled.div`
    ${headingFontMixin};
    ${whiteTextMixin};
    font-size: 0.875rem;
    font-weight: 600;
`;

const Amount = styled.div`
    ${headingFontMixin};
    ${whiteTextMixin};
    font-size: 1.25rem;
    line-height: 120%;
    font-weight: 600;
`;

const Group = styled.div``;

const BatteryCardLayout = styled.div`
    display: grid;
    grid-template-columns: 11.25rem 1fr;
    gap: 1.25rem;
    align-items: stretch;
    height: 100%;
`;

const BatteryDataContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const BatteryHeadline = styled.div`
    ${headingFontMixin};
    ${whiteTextMixin};
    font-size: 2.5rem;
    line-height: 100%;
    margin-top: 0.5rem;
    margin-bottom: 1.875rem;
`;

const BatteryLabel = styled.div`
    ${headingFontMixin};
    ${whiteTextMixin};
    font-size: 2.5rem;
    line-height: 120%;
    margin-bottom: 1rem;
`;

const BatterySubLabel = styled.div`
    ${headingFontMixin};
    ${whiteTextMixin};
    font-size: 0.875rem;
    line-height: 120%;
    font-weight: 600;
`;

const BatteryBottomMetrics = styled.div`
    margin-top: auto;
    display: flex;
    justify-content: space-between;
`;

const BatteryImageWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AnimatedBatteryIcon = styled(Icon)<{ $fillPercent: number }>`
  ${({ $fillPercent }) => css`
    .battery-fill {
      clip-path: inset(${100 - $fillPercent}% 0 0 0);
      transition: clip-path 700ms ease;
      will-change: clip-path;
    }
  `}
`;

const options = [
  { value: RevenueOptionsType.revenue, label: 'Revenue' },
  { value: RevenueOptionsType.revenueMW, label: 'Revenue / MW' },
];

Chart.register(CategoryScale);
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

const NewDemoPage = () => {
  const { isLoading: isLoadingDevices } = useFetchSites();
  const { isLoading: isLoadingSocAndBalancing} = useFetchChartSocAndBalancing();
  const { isLoading: isLoadingPowerAndBalancing} = useFetchChartPowerAndBalancing();
  const { isLoading: isLoadingFrequencyAndBalancing} = useFetchChartFrequencyAndBalancing();
  const { isLoading: isLoadingRevenue } = useFetchRevenue();
  const { isLoading: isLoadingRevenueTotal } = useFetchRevenueTotal();
  const { isLoading: isLoadingBattery } = useFetchBattery();
  const { isLoading: isLoadingRevenueByMarket } = useFetchRevenueByMarket();
  const { chartData: ffrEnergyPriceChartData } = useFetchFfrEnergyPrice();
  const { chartData: ffrCapacityPriceChartData } = useFetchFfrCapacityPrice();
  const { chartData: ffrEnergyVolumesChartData } = useFetchFfrEnergyVolumes();
  const { chartData: ffrCapacityVolumesChartData } = useFetchFfrCapacityVolumes();

  const [selected, setSelected] = useState<RevenueOptionsType>(RevenueOptionsType.revenue);
  const { socAndBalancingStore, powerAndBalancingStore, frequencyAndBalancingStore, revenueStore, batteryStore, settingsStore, revenueTotalStore, revenueByMarketStore } = useStore();
  const { socAndBalancing, version: versionSocAndBalancing } = socAndBalancingStore;
  const { powerAndBalancing, version: versionPowerAndBalancing } = powerAndBalancingStore;
  const { frequencyAndBalancing, version: versionFrequencyAndBalancing } = frequencyAndBalancingStore;
  const { revenue: revenueData } = revenueStore;
  const { batteryData } = batteryStore;
  const { selectedDate, siteList, selectedSiteId } = settingsStore;
  const { revenueTotal } = revenueTotalStore;
  const { revenueByMarketData } = revenueByMarketStore;
  const primaryBattery = batteryData[0];
  const batterySocPercentRaw = primaryBattery ? Number(primaryBattery.soC.amount) : 0;
  const batterySocPercent = Number.isFinite(batterySocPercentRaw)
    ? Math.min(100, Math.max(0, batterySocPercentRaw))
    : 0;
  const batterySocText = primaryBattery ? `${primaryBattery.soC.amount}${primaryBattery.soC.unit}` : '--';
  const batteryCapacityText = primaryBattery ? `${primaryBattery.capacity.amount}${primaryBattery.capacity.unit}` : '--';
  const batteryName = primaryBattery?.name || 'Battery SOC';
  const currentTemp = primaryBattery?.currentBatteryTemp ? `${primaryBattery.currentBatteryTemp.amount}°${primaryBattery.currentBatteryTemp.unit}` : '--';
  const maxTemp = primaryBattery?.maxBatteryTempThisMonth ? `${primaryBattery.maxBatteryTempThisMonth.amount}°${primaryBattery.maxBatteryTempThisMonth.unit}` : '--';
  const minTemp = primaryBattery?.minBatteryTempThisMonth ? `${primaryBattery.minBatteryTempThisMonth.amount}°${primaryBattery.minBatteryTempThisMonth.unit}` : '--';

  const { formatToDecimals } = useFormattingUtils();
  const {
    handleRevenueCsvExport,
    handleSocTimeSeriesCsvExport,
    handleBalancingEventsCsvExport,
    handleFfrEnergyPriceCsvExport,
    handleFfrCapacityPriceCsvExport,
    handleFfrEnergyTradedAndActivationsCsvExport,
    handleFfrCapacityTradedAndActivationsCsvExport,
  } = useNewDemoPageCsvExports({
    selectedDate,
    selectedSiteId,
    selectedRevenueOption: selected,
    revenueData,
    socAndBalancing,
    ffrEnergyPriceChartData,
    ffrCapacityPriceChartData,
    energyTradedAndActivationsChartData: ffrEnergyVolumesChartData,
    capacityTradedAndActivationsChartData: ffrCapacityVolumesChartData,
  });

  return (
    <Layout site={''}>
        <FleetNavigation
          isLoading={isLoadingDevices}
          fleetOptions={siteList}
          currentFleetId={selectedSiteId}
          date={selectedDate}
        />

      <Grid $revenueColumns>
        <Column>
          {/*Revenue generated*/}
          <TitleWhiteBox backgroundColor={'linear-gradient(92.48deg, #E8513F 2.65%, #FDBF2D 99.08%)'}>
            <FlexboxRow>
              <Group>
                <StyledHeader level={'h3'} color={colors.primaryWhite}>Revenue generated</StyledHeader>
                <Header level={'h6'} color={colors.primaryWhite}>{selectedDate}</Header>
              </Group>
              <Icon icon={iconDots} size={40} />
            </FlexboxRow>

            <Group>
              <AmountHeader>DAY</AmountHeader>
              <InlineText>
                <Price level={'h6'}>{formatToDecimals(revenueTotal.dayRevenue.amount)}</Price>
                <Currency level={'h6'}>{revenueTotal.dayRevenue.currency}</Currency>
              </InlineText>
            </Group>

            <InlineTextJustified>
              <Group>
                <AmountHeader>MONTH</AmountHeader>
                <Amount>{formatToDecimals(revenueTotal.revenueMonth.amount)} {revenueTotal.revenueMonth.currency}</Amount>
              </Group>
              <Group>
                <AmountHeader>YEAR</AmountHeader>
                <Amount>{formatToDecimals(revenueTotal.revenueYear.amount)} {revenueTotal.revenueYear.currency}</Amount>
              </Group>
              <Group>
                <AmountHeader>TOTAL</AmountHeader>
                <Amount>{formatToDecimals(revenueTotal.revenueAllTime.amount)} {revenueTotal.revenueAllTime.currency}</Amount>
              </Group>
            </InlineTextJustified>
          </TitleWhiteBox>
        </Column>
        <Column>
          <WhiteBox>
            <FlexboxRow $mb={12}>
              <FlexboxRow>
                <CustomRadioGroup options={options} name="revenueChartradioButtons" selectedValue={selected} onChange={setSelected} />
              </FlexboxRow>
              <Button styleType={'link'} onClick={handleRevenueCsvExport}>Export to .CSV</Button>
            </FlexboxRow>
            <RevenueBarChartArea>
              <Suspense fallback={<div>Loading...</div>}>
                <RevenueBarChart data={revenueData} dataType={selected} />
              </Suspense>
            </RevenueBarChartArea>
          </WhiteBox>
        </Column>

        <Column>
          <WhiteBox>
            <RevenueDonutChart data={revenueByMarketData} />
          </WhiteBox>
        </Column>
      </Grid>

      <SectionHeader level={"h4"}> SOC</SectionHeader>

      <Grid $batteryColumns>
        <Column>
          <WhiteBox>
            <PositionAbsoluteHeader>
              <Group>
                <Button styleType={'link'} onClick={handleSocTimeSeriesCsvExport}>Export: SOC time series</Button>
                <Button styleType={'link'} onClick={handleBalancingEventsCsvExport}>Export: Balancing events</Button>
              </Group>
            </PositionAbsoluteHeader>
            <StepChartArea>
              <Suspense fallback={<div>Loading...</div>}>
                <SocAndBalancingStepChart key={versionSocAndBalancing} data={socAndBalancing} />
              </Suspense>
            </StepChartArea>
          </WhiteBox>
        </Column>

        {/*-----Battery picture-----*/}
        <Column>
          <TitleWhiteBox backgroundColor={'linear-gradient(92.48deg, #E8513F 2.65%, #FDBF2D 99.08%)'}>
            <BatteryCardLayout>
              <BatteryImageWrap>
                <AnimatedBatteryIcon icon={imageBattery} size={270} $fillPercent={batterySocPercent} />
              </BatteryImageWrap>

              <BatteryDataContent>
                <FlexboxRow>
                  <StyledHeader level={'h3'} color={colors.primaryWhite}>{batteryName} <br /> SOC</StyledHeader>
                  <Icon icon={iconDots} size={40} />
                </FlexboxRow>

                <BatteryHeadline>{batterySocText} | {batteryCapacityText}</BatteryHeadline>

                <BatterySubLabel>Current battery <br />temperature</BatterySubLabel>
                <BatteryLabel>{currentTemp}</BatteryLabel>

                <BatteryBottomMetrics>
                  <Group>
                    <BatterySubLabel>Maximum this <br /> month</BatterySubLabel>
                    <BatteryLabel>{maxTemp}</BatteryLabel>
                  </Group>

                  <Group>
                    <BatterySubLabel>Minimum this <br /> month</BatterySubLabel>
                    <BatteryLabel>{minTemp}</BatteryLabel>
                  </Group>
                </BatteryBottomMetrics>
              </BatteryDataContent>
            </BatteryCardLayout>
          </TitleWhiteBox>
        </Column>
      </Grid>

      <Grid>
        <Column>
          <SectionHeader level={"h4"}>Power and Balancing</SectionHeader>
          <WhiteBox>
            <StepChartArea>
              <Suspense fallback={<div>Loading...</div>}>
                <PowerAndBalancingStepChart key={versionPowerAndBalancing} data={powerAndBalancing} />
              </Suspense>
            </StepChartArea>
          </WhiteBox>
        </Column>

        <Column>
          <SectionHeader level={"h4"}>Frequency and Balancing</SectionHeader>
          <WhiteBox>
            <StepChartArea>
              <Suspense fallback={<div>Loading...</div>}>
                <FrequencyAndBalancingStepChart key={versionFrequencyAndBalancing} data={frequencyAndBalancing} />
              </Suspense>
            </StepChartArea>
          </WhiteBox>
        </Column>
      </Grid>

      <Grid4k>
      <Grid>
            <Column>
              <SectionHeader level={"h4"}>FFR energy price</SectionHeader>
              <WhiteBox height="24.875rem">
                <PositionAbsoluteHeader>
                  <Group>
                    <Button styleType={'link'} onClick={handleFfrEnergyPriceCsvExport}>Export to .CSV</Button>
                  </Group>
                </PositionAbsoluteHeader>
                <StackedBarChart data={ffrEnergyPriceChartData}/>
              </WhiteBox>
            </Column>

            <Column>
              <SectionHeader level={"h4"}>FFR capacity price</SectionHeader>
              <WhiteBox height="24.875rem">
                <PositionAbsoluteHeader>
                  <Group>
                    <Button styleType={'link'} onClick={handleFfrCapacityPriceCsvExport}>Export to .CSV</Button>
                  </Group>
                </PositionAbsoluteHeader>
                <StackedBarChart data={ffrCapacityPriceChartData}/>
              </WhiteBox>
            </Column>
          </Grid>

      <SectionHeader level={"h4"}>FFR energy traded volumes & activations</SectionHeader>

      <Grid $oneColumn>
        <Column>
          <WhiteBox height="24.875rem">
            <PositionAbsoluteHeader>
              <Group>
                <Button styleType={'link'} onClick={handleFfrEnergyTradedAndActivationsCsvExport}>Export to .CSV</Button>
              </Group>
            </PositionAbsoluteHeader>
            <StackedBarChart data={ffrEnergyVolumesChartData}/>
          </WhiteBox>
        </Column>
      </Grid>


      <Grid $oneColumn>
        <Column>
          <SectionHeader level={"h4"}>FFR capacity traded volumes & activations</SectionHeader>

          <WhiteBox height="24.875rem">
            <PositionAbsoluteHeader>
              <Group>
                <Button styleType={'link'} onClick={handleFfrCapacityTradedAndActivationsCsvExport}>Export to .CSV</Button>
              </Group>
            </PositionAbsoluteHeader>
            <StackedBarChart data={ffrCapacityVolumesChartData}/>
          </WhiteBox>
        </Column>
      </Grid>
      </Grid4k>

      <SectionHeader level={"h4"}>Sites</SectionHeader>

      <Grid $oneColumn>
        <Column>
          <ScrollTableWhiteBox disablePadding>
            <BatteryDataTable data={batteryData}/>
          </ScrollTableWhiteBox>
        </Column>
      </Grid>
      </Layout>
  );
};

export default observer(NewDemoPage);
